<template>
  <div class="preferences-password">
    <span class="preferences-password__title">{{ $t('page.admin-user-profile.change-password') }} </span>
    <form @submit.prevent="submitPasswords" class="preferences-password__form">
      <div>
        <FormInput
          v-model="oldPassword"
          ref="oldPassword"
          name="oldPassword"
          :type="oldPasswordType"
          :error="oldPasswordError"
          :label="$t('reset-password.old-password-input')"
          v-validate="'required'"
          has-gray-background
          autocomplete="on"
        >
          <template #top-right>
            <button
              class="reset-password-form__toggle-password"
              type="button"
              @click.prevent="toggleOldPassword"
              tabindex="-1"
            >
              <BaseIcon name="eye" v-if="oldPasswordHidden" />
              <BaseIcon name="eye-off" v-else />
              {{ oldPasswordHidden ? $t('reset-password.see') : $t('reset-password.hide') }}
            </button>
          </template>
        </FormInput>
        <FormInput
          v-model="newPassword"
          name="password"
          ref="password"
          :type="newPasswordType"
          :error="newPasswordError"
          :label="$t('reset-password.password-input')"
          v-validate="{ is_not: oldPassword, required: true }"
          has-gray-background
          autocomplete="on"
        >
          <template #top-right>
            <button
              class="reset-password-form__toggle-password"
              type="button"
              @click.prevent="toggleNewPassword"
              tabindex="-1"
            >
              <BaseIcon name="eye" v-if="newPasswordHidden" />
              <BaseIcon name="eye-off" v-else />
              {{ newPasswordHidden ? $t('reset-password.see') : $t('reset-password.hide') }}
            </button>
          </template>
        </FormInput>
        <FormInput
          v-model="confirmation"
          name="confirmation"
          ref="confirmation"
          :type="confirmationType"
          :error="confirmationError"
          :label="$t('reset-password.confirmation-input')"
          v-validate="'required|confirmed:password'"
          has-gray-background
          autocomplete="on"
        >
          <template #top-right>
            <button
              class="reset-password-form__toggle-password"
              type="button"
              @click.prevent="toggleConfirmation"
              tabindex="-1"
            >
              <BaseIcon name="eye" v-if="confirmationHidden" />
              <BaseIcon name="eye-off" v-else />
              {{ confirmationHidden ? $t('reset-password.see') : $t('reset-password.hide') }}
            </button>
          </template>
        </FormInput>
        <PasswordCriteria :password="newPassword" />
        <ErrorNotice v-if="errorFormNotice && !saving" :error="errorFormNotice" />
      </div>
      <div class="preferences-password__actions">
        <BaseLink class="preferences-password__forgot" is-small to="/forgot-password">
          {{ $t('login.forgot-pwd') }}
        </BaseLink>
        <div class="preferences-password__button">
          <FormButton :isLoading="saving" type="submit" :isOutlined="!isFormValid" :disabled="!isFormValid">
            {{ $t('reset-password.button') }}
          </FormButton>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
import BaseIcon from '@/components/base/BaseIcon';
import BaseLink from '@/components/base/BaseLink';
import ErrorNotice from '@/components/notice/ErrorNotice';
import FormButton from '@/components/form/FormButton.vue';
import FormInput from '@/components/form/FormInput.vue';
import PasswordCriteria from '@/components/PasswordCriteria';
import { validatePassword } from '@/util/password-util';

import { mapActions, mapGetters } from 'vuex';

export default {
  name: 'PreferencesPasswordForm',
  components: {
    BaseIcon,
    BaseLink,
    ErrorNotice,
    FormButton,
    FormInput,
    PasswordCriteria,
  },
  props: {
    userId: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      oldPassword: '',
      newPassword: '',
      confirmation: '',
      oldPasswordHidden: true,
      newPasswordHidden: true,
      confirmationHidden: true,
      errorFormNotice: null,
    };
  },
  computed: {
    ...mapGetters('changePassword', ['saving']),
    isFormValid() {
      return (
        this.passwordCriteria.valid &&
        this.newPassword === this.confirmation &&
        this.oldPassword !== '' &&
        this.newPassword !== this.oldPassword
      );
    },
    passwordCriteria() {
      return validatePassword(this.newPassword);
    },
    oldPasswordError() {
      const formError = this.errors.items.find((x) => x.field === 'oldPassword');

      return formError ? formError.msg : undefined;
    },
    newPasswordError() {
      const formError = this.errors.items.find((x) => x.field === 'password');

      return formError ? formError.msg : undefined;
    },
    confirmationError() {
      const formError = this.errors.items.find((x) => x.field === 'confirmation');

      return formError ? formError.msg : undefined;
    },
    oldPasswordType() {
      return this.oldPasswordHidden ? 'password' : 'text';
    },
    newPasswordType() {
      return this.newPasswordHidden ? 'password' : 'text';
    },
    confirmationType() {
      return this.confirmationHidden ? 'password' : 'text';
    },
  },
  methods: {
    ...mapActions('changePassword', ['updatePassword']),
    submitPasswords() {
      this.$validator.validate();
      if (this.isFormValid) {
        this.updatePassword({
          passwords: {
            oldPassword: this.oldPassword,
            newPassword: this.newPassword,
          },
          userId: this.userId,
        })
          .then(() => {
            this.$toast.success(this.$t('reset-password.success-subtitle'));
            this.$emit('password-has-changed');
          })
          .catch((e) => {
            this.errorFormNotice = e;
            this.$toast.error(e);
          })
          .finally(() => {
            this.resetForm();
          });
      }
    },
    resetForm() {
      this.oldPassword = '';
      this.newPassword = '';
      this.confirmation = '';
      this.oldPasswordHidden = true;
      this.newPasswordHidden = true;
      this.confirmationHidden = true;
      this.$validator.reset();
      this.errors.clear();
    },
    toggleOldPassword() {
      this.oldPasswordHidden = !this.oldPasswordHidden;
    },
    toggleNewPassword() {
      this.newPasswordHidden = !this.newPasswordHidden;
    },
    toggleConfirmation() {
      this.confirmationHidden = !this.confirmationHidden;
    },
  },
  beforeDestroy() {
    this.errorFormNotice = null;
  },
};
</script>

<style lang="scss">
.preferences-password {
  width: 100%;
  background: $color-white;
  box-shadow: $shadow-default;
  padding: 2rem;
  border-radius: $border-radius-root;
  margin-top: 16px;

  &__form {
    height: calc(100% - 56px);
    display: flex;
    justify-content: space-between;
    flex-direction: column;
    min-height: 460px;
  }

  &__title {
    font-weight: bold;
    font-size: 20px;
    padding-bottom: 32px;
    display: block;
  }

  &__actions {
    display: flex;
    justify-content: flex-end;
  }

  &__forgot {
    margin-right: 16px;
    align-self: center;
  }

  @media screen and (max-width: 468px) {
    &__actions {
      justify-content: flex-end;
      padding-top: 32px;
      flex-direction: column-reverse;
    }
    &__forgot {
      justify-content: flex-end;
      margin-right: 0px;
      padding-top: 12px;
    }
    &__button {
      display: flex;
      width: 100%;
      justify-content: flex-end;
    }
  }
}
</style>
